import React, { Fragment, useMemo, useState } from 'react'
import {
	ContentCopyOutlined,
	DriveFileMoveOutlined,
	EditRounded,
	Refresh,
} from '@mui/icons-material'
import Popup from '../../../ui/Popup'
import {
	MoveCopyItemStatus,
	useProgressContext,
} from '../../../../context/progressContext'
import ProgressItemsList from './ProgressItemsList'
import FilterButtons from './FilterButtons'
import {
	useMoveObject,
	useCopyObject,
} from '../../../../hooks/api/documentHooks'
import InputModal from '../../common/InputModal'
import IconButton from '../../../ui/IconButton'
import { getFileNameAndExt } from '../../../../lib/utils/formatDocument'
import {
	FILE_SYSTEM_NAME_MAX_LENGTH,
	getFileBaseNameMaxLength,
} from '../../../../lib/schemas'

interface MoveCopyProgressPopupProps {
	items: MoveCopyItemStatus[]
	isOpen: boolean
	onClose: () => void
}

type Status = MoveCopyItemStatus['status']
type ItemsFilter = 'all' | 'completed' | 'failed'

const MoveCopyProgressPopup: React.FC<MoveCopyProgressPopupProps> = ({
	items,
	isOpen,
	onClose,
}) => {
	const [filter, setFilter] = useState<ItemsFilter>('all')
	const [retryItem, setRetryItem] = useState<MoveCopyItemStatus | null>(null)
	const [isRetrying, setIsRetrying] = useState(false)
	const { setMoveCopyStatuses } = useProgressContext()
	const { mutateAsync: copyObject } = useCopyObject()
	const { mutateAsync: moveObject } = useMoveObject()

	const currentOperationType =
		items.find((item) => item.status === 'pending' || item.status === 'processing')
			?.operationType ??
		items[0]?.operationType ??
		'move'

	const filteredByOperationType = useMemo(
		() =>
			items.filter(
				(item) => (item.operationType ?? currentOperationType) === currentOperationType,
			),
		[items, currentOperationType],
	)

	const mappedItems = useMemo(() => {
		const filtered = filter === 'all'
			? filteredByOperationType
			: filteredByOperationType.filter((item) => item.status === filter)

		const statusOrder: Record<Status, number> = {
			processing: 1,
			pending: 2,
			completed: 3,
			failed: 3,
		}

		return [...filtered].sort(
			(a, b) => statusOrder[a.status] - statusOrder[b.status],
		)
	}, [filter, filteredByOperationType])

	const totalCompleted = filteredByOperationType.filter(({ status }) =>
		['completed', 'failed'].includes(status),
	).length
	const totalItems = filteredByOperationType.length

	const filterButtons: { label: string; value: ItemsFilter }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'Completed', value: 'completed' },
		{ label: 'Failed', value: 'failed' },
	]

	const filterCounts: Record<ItemsFilter, number> = {
		all: filteredByOperationType.length,
		completed: filteredByOperationType.filter((f) => f.status === 'completed')
			.length,
		failed: filteredByOperationType.filter((f) => f.status === 'failed').length,
	}

	const operationLabel = currentOperationType === 'copy' ? 'Copy' : 'Move'
	const operationIcon =
		currentOperationType === 'copy' ? (
			<ContentCopyOutlined className="fill-text-secondary w-5 h-5 dark:fill-text-main-dark" />
		) : (
			<DriveFileMoveOutlined className="fill-text-secondary w-5 h-5 dark:fill-text-main-dark" />
		)

	const handleRetry = async (newName: string) => {
		if (!retryItem) return

		setIsRetrying(true)
		let operationCompleted = false

		try {
			const isFile = retryItem.type === 0
			const fullName = isFile
				? (() => {
						const ext = getFileNameAndExt(retryItem.name)?.ext
						return `${newName}${ext ? '.' + ext : ''}`
				  })()
				: newName

			const baseData = {
				data: {
					id: retryItem.id,
					type: retryItem.type,
					name: fullName,
				},
				parentId: retryItem.parentId || '',
				invalidate: true,
				onProgress: (
					itemId: string,
					status: 'processing' | 'completed' | 'failed',
					error?: string
				) => {
					if (status === 'completed') {
						operationCompleted = true
					}
					setMoveCopyStatuses((prev) =>
						prev.map((item) => {
							if (item.id === itemId) {
								return { ...item, status, error }
							}
							return item
						})
					)
				},
				autoRename: false,
			}

			if (retryItem.operationType === 'copy') {
				const result = await copyObject(baseData)
				await new Promise((resolve) => setTimeout(resolve, 100))
				if (operationCompleted || result?.data?.success) {
					setRetryItem(null)
				}
			} else {
				const result = await moveObject({
					...baseData,
					sourceFolderId: retryItem.sourceFolderId,
				})
				await new Promise((resolve) => setTimeout(resolve, 100))
				if (operationCompleted || result?.data?.success) {
					setRetryItem(null)
				}
			}
		} catch (error) {
			console.log('error in handleRetry', error)
		} finally {
			setIsRetrying(false)
		}
	}

	const renderActions = (item: MoveCopyItemStatus) => {
		if (item.status !== 'failed' || item.error !== 'FILE_SAME_NAME_EXISTS') {
			return null
		}

		return (
			<IconButton
				variant="secondary"
				className="w-9 h-9 box-border"
				icon={<Refresh className="w-5 h-5 dark:fill-text-main-dark" />}
				tooltip="Retry with new name"
				onClick={() => setRetryItem(item)}
			/>
		)
	}
	const retryFileExtension =
		retryItem?.type === 0 ? getFileNameAndExt(retryItem.name)?.ext : undefined
	const retryNameMaxLength =
		retryItem?.type === 0
			? getFileBaseNameMaxLength(
					retryFileExtension ? `.${retryFileExtension}` : '',
				)
			: FILE_SYSTEM_NAME_MAX_LENGTH

	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			icon={operationIcon}
			title={
				<Fragment>
					<span className="dark:text-white">{operationLabel}</span>
					<span className="font-normal text-base text-text-secondary dark:text-text-main-dark">
						{' '}
						{totalCompleted} of {totalItems}
					</span>
				</Fragment>
			}
			relativePosition={true}
			className="dark:text-text-main-dark dark:bg-background-light-dark"
		>
			<FilterButtons
				buttons={filterButtons}
				activeFilter={filter}
				onFilterChange={setFilter}
				counts={filterCounts}
			/>
			{mappedItems && mappedItems.length > 0 ? (
				<ProgressItemsList
					items={mappedItems}
					filterTechnicalErrors={true}
					getItemKey={(item, index) =>
						`${item.id}_${item.operationId || index}_${index}`
					}
					getDisplayName={(item) => item.name}
					isFolder={(item) => item.type === 1}
					renderActions={renderActions}
				/>
			) : (
				<div className="flex items-center justify-center min-h-56 max-h-56 px-0.5 mt-3">
					<p className="text-text-secondary text-xs">No items</p>
				</div>
			)}

			{retryItem && (
				<InputModal
					isOpen={!!retryItem}
					onClose={() => setRetryItem(null)}
					title={`Rename ${retryItem.type === 1 ? 'folder' : 'file'}`}
					icon={<EditRounded />}
					defaultValue={
						retryItem.type === 1
							? retryItem.name
							: getFileNameAndExt(retryItem.name)?.name || retryItem.name
					}
					inputLabel="Name"
					inputPlaceholder={`${retryItem.type === 1 ? 'Folder' : 'File'} name`}
					actionLabel="Save"
					loading={isRetrying}
					onAction={handleRetry}
					maxLength={retryNameMaxLength}
				/>
			)}
		</Popup>
	)
}

export default MoveCopyProgressPopup
